import { sendGAEvent, sendGTMEvent } from "@next/third-parties/google";
import FrameConfig from "@lib/classes/FrameConfig";
import Session from "@utils/session";

/**
 * Get current service area data from FrameConfig or Session.
 */
export const getServiceAreaData = () => {
  const frameConfig = new FrameConfig();
  return {
    serviceArea: frameConfig.serviceArea || Session.getBooking()?.serviceArea,
    serviceAreaName: frameConfig.serviceAreaName || Session.getBooking()?.serviceAreaName,
  };
};

/**
 * Push data to the GTM dataLayer without triggering an event.
 * Automatically includes service area data.
 */
export const pushData = (data?: Record<string, any>) => {
  if (!global.window) return;
  if (typeof window === "undefined" || !window.dataLayer) return;

  const { serviceArea, serviceAreaName } = getServiceAreaData();
  window.dataLayer.push({
    ...data,
    ecommerce: {
      service_area: serviceArea,
      service_area_name: serviceAreaName,
      ...data?.ecommerce,
    },
  });
};

/**
 * Centralized function to send analytics events to enabled tracking services.
 * Checks if GTM/GA4 are initialized before sending to avoid console warnings.
 */
const sendAnalyticsEvent = (
  eventName: string,
  ga4EventData?: Record<string, any>,
  gtmEventData?: Record<string, any>
) => {
  if (!global.window) return;

  // Send to GTM only if dataLayer exists (GTM is initialized)
  if (gtmEventData && typeof window !== "undefined" && window.dataLayer) {
    sendGTMEvent({ event: eventName, ...gtmEventData });
  }

  // Send to GA4 only if gtag function exists (GA4 is initialized)
  // sendGAEvent signature: sendGAEvent('event', eventName, eventData)
  if (ga4EventData && typeof window !== "undefined" && window.gtag) {
    sendGAEvent("event", eventName, ga4EventData);
  }
};

export const event = (eventName: string, eventData?: Record<string, any>) => {
  const { serviceArea, serviceAreaName } = getServiceAreaData();
  eventData ||= {};

  sendAnalyticsEvent(
    `BE:${eventName}`,
    {
      ...eventData,
      service_area: serviceArea,
      service_area_name: serviceAreaName,
    },
    {
      ...eventData,
      ecommerce: { service_area: serviceArea, service_area_name: serviceAreaName },
    }
  );
};

/**
 * Build checkout/cart data from a booking.
 * Returns null if the booking doesn't have valid cart data (no active legs with services).
 */
export const buildCheckoutData = (booking?: be.Booking, couponName?: string) => {
  if (!booking) return null;

  const activeLegs = booking.activeLegs();
  // Only include cart data if there are legs with selected services
  if (!activeLegs.length || !activeLegs.some((leg: be.Reservation) => leg.service)) {
    return null;
  }

  const parentUrl = new FrameConfig().parentUrl;

  return {
    value: booking.total(),
    bookingPage: parentUrl ? parentUrl.split("?")[0] : undefined,
    currency: global.window?.currency,
    coupon: couponName,
    service_area: booking.serviceArea,
    service_area_name: booking.serviceAreaName || new FrameConfig().serviceAreaName,
    items: activeLegs.map((leg: be.Reservation, i: number) => ({
      item_id: leg.resId,
      item_name: `${leg.pickup?.displayName} => ${leg.dropoff?.displayName}`,
      price: leg.service?.fareDisplay,
      index: i,
      coupon: couponName,
      item_category: leg.service?.name,
      quantity: 1,
    })),
  };
};

export const beginCheckout = (booking: be.Booking, couponName?: string) => {
  const data = buildCheckoutData(booking, couponName);
  if (!data) return;

  sendAnalyticsEvent("begin_checkout", data, { ecommerce: data });
};

export const selectItem = (leg: be.Reservation, service: be.Service) => {
  if (!leg || !service) return;
  const parentUrl = new FrameConfig().parentUrl;

  const data = {
    bookingPage: parentUrl ? parentUrl.split("?")[0] : undefined,
    service_area: Session.getBooking().serviceArea,
    service_area_name: Session.getBooking()?.serviceAreaName || new FrameConfig().serviceAreaName,
    item_list_name: `${leg.pickup?.displayName} => ${leg.dropoff?.displayName}`,
    items: [
      {
        item_id: service.serviceType,
        item_name: service.name,
        price: service.fareDisplay,
      },
    ],
  };

  sendAnalyticsEvent("select_item", data, { ecommerce: data });
};

export const viewItemList = (leg: be.Reservation, services: be.Service[]) => {
  if (!leg || !services) return;
  const parentUrl = new FrameConfig().parentUrl;

  const data = {
    bookingPage: parentUrl ? parentUrl.split("?")[0] : undefined,
    service_area: Session.getBooking().serviceArea,
    service_area_name: Session.getBooking()?.serviceAreaName || new FrameConfig().serviceAreaName,
    item_list_name: `${leg.pickup?.displayName} => ${leg.dropoff?.displayName}`,
    items: services.map((service: be.Service, i: number) => ({
      item_id: service.serviceType,
      item_name: service.name,
      price: service.fareDisplay,
      index: i,
    })),
  };

  sendAnalyticsEvent("view_item_list", data, { ecommerce: data });
};

export const stepView = (stepName: string, booking?: be.Booking, couponName?: string) => {
  event(`view_${stepName}`, buildCheckoutData(booking, couponName) ?? undefined);
};

export const purchase = (booking: be.Booking, couponName?: string) => {
  if (!booking || booking.edit) return;
  const parentUrl = new FrameConfig().parentUrl;

  const data = {
    bookingPage: parentUrl ? parentUrl.split("?")[0] : undefined,
    transaction_id: booking.itineraryId,
    value: booking.total(),
    currency: global.window.currency,
    coupon: couponName,
    service_area: booking.serviceArea,
    service_area_name: booking.serviceAreaName || new FrameConfig().serviceAreaName,
    items: booking.activeLegs().map((leg: be.Reservation, i: number) => ({
      item_id: leg.resId,
      item_name: `${leg.pickup?.displayName} => ${leg.dropoff?.displayName}`,
      price: leg.service?.fareDisplay,
      index: i,
      coupon: couponName,
      item_category: leg.service?.name,
      quantity: 1,
    })),
  };

  sendAnalyticsEvent("purchase", data, { ecommerce: data });
};
